import { useState, useEffect, useCallback } from 'react';
import { API_V1, authFetch } from '../constants';
import StatusBadge from '../components/ui/StatusBadge';
import Modal from '../components/ui/Modal';
import PageLayout from '../components/ui/PageLayout';
import KpiGrid from '../components/ui/KpiGrid';
import DataTable, { Column } from '../components/ui/DataTable';
import ActionButton from '../components/ui/ActionButton';
import { useGlobalEntity, withEntityParam } from '../hooks/useGlobalEntity';
import { useEntities } from '../hooks/useEntities';

const API = `${API_V1}/bank-accounts`;

export default function BankAccounts() {
  const { entity } = useGlobalEntity();
  const entities = useEntities();
  const [accounts, setAccounts] = useState<any[]>([]);
  const [summary, setSummary] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);
  // Phase 9.6 -- currency default empty so the entity onChange handler
  // can auto-fill it from entity_registry.functional_currency (via the
  // entities catalog). Operator can still override after auto-fill.
  const [form, setForm] = useState({ account_name: '', account_number: '', bank_name: '', routing_number: '', iban: '', swift_code: '', account_type: 'OPERATING', currency: '', gl_account: '', entity_code: '' });
  const [currencyAutoFilled, setCurrencyAutoFilled] = useState(false);
  const [suggestions, setSuggestions] = useState<any[]>([]);

  // Phase 9.6 -- entity change derives currency from
  // entity_registry.functional_currency. Mirrors the backend
  // infer_currency_from_entity() helper so the UI shows what will
  // actually get written.
  const handleEntityChange = (ec: string) => {
    const match = entities.find(e => e.entity_code === ec);
    if (match?.functional_currency) {
      setForm(prev => ({ ...prev, entity_code: ec, currency: match.functional_currency }));
      setCurrencyAutoFilled(true);
    } else {
      setForm(prev => ({ ...prev, entity_code: ec }));
      setCurrencyAutoFilled(false);
    }
  };

  const showToast = (msg: string, ok: boolean) => { setToast({ msg, ok }); setTimeout(() => setToast(null), 4000); };
  const inputStyle: React.CSSProperties = { padding: '7px 12px', fontSize: 13, border: '1px solid var(--border-subtle)', borderRadius: 6, background: 'var(--bg-card)', color: 'var(--text-primary)', width: '100%', outline: 'none' };

  const load = useCallback(async () => {
    setLoading(true);
    const [listRes, sumRes] = await Promise.all([
      authFetch(withEntityParam(API, entity)),
      authFetch(withEntityParam(`${API}/summary`, entity)),
    ]);
    if (listRes.ok) { const d = await listRes.json(); setAccounts(d.items || []); }
    if (sumRes.ok) setSummary(await sumRes.json());
    setLoading(false);
  }, [entity]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    const res = await authFetch(API, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form) });
    if (res.ok) {
      const d = await res.json();
      const glMsg = d.gl_account ? ` -> GL ${d.gl_account}` : '';
      showToast(`Bank account created${glMsg}`, true);
      setShowCreate(false);
      load();
    }
    else { const e = await res.json(); showToast(e.detail || 'Error', false); }
  };

  const handleAutoDetect = async () => {
    const res = await authFetch(`${API}/auto-detect`, { method: 'POST' });
    if (res.ok) { const d = await res.json(); showToast(`Auto-detected: ${d.detected} found, ${d.created} created, ${d.skipped} already registered`, true); load(); }
    else showToast('Auto-detect failed', false);
  };

  // Phase 8 -- discover banks from GL activity. Surfaces a suggestion
  // (read-only) the operator can apply by clicking 'Auto-create'.
  const handleDiscoverFromGL = async () => {
    const res = await authFetch(`${API}/discover-from-gl`);
    if (res.ok) {
      const d = await res.json();
      const s = d.suggestions || [];
      if (s.length === 0) {
        showToast(d.reason || 'No suggestions from GL activity', true);
      }
      setSuggestions(s);
    } else {
      showToast('Discover failed', false);
    }
  };

  const handleApplySuggestion = async (sug: any) => {
    const payload = {
      bank_name: sug.proposed_bank_name,
      currency: sug.proposed_currency,
      account_type: sug.proposed_account_type,
      entity_code: sug.proposed_entity_code || null,
    };
    const res = await authFetch(`${API}/apply-suggestion`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (res.ok) {
      const d = await res.json();
      showToast(`Created ${d.account_name} -> GL ${d.gl_account}`, true);
      setSuggestions([]);
      load();
    } else {
      const e = await res.json();
      showToast(e.detail || 'Apply failed', false);
    }
  };

  const handleDeactivate = async (id: number) => {
    const res = await authFetch(`${API}/${id}/deactivate`, { method: 'POST' });
    if (res.ok) { showToast('Account deactivated', true); load(); }
    else showToast('Failed', false);
  };

  const columns: Column[] = [
    { key: 'account_name', label: 'Account Name', render: (v: any) => <span style={{ fontWeight: 600 }}>{v}</span> },
    { key: 'bank_name', label: 'Bank', render: (v: any) => v || '-' },
    { key: 'account_number', label: 'Account #', render: (v: any) => v ? `****${v.slice(-4)}` : '-' },
    { key: 'account_type', label: 'Type' },
    { key: 'currency', label: 'Currency' },
    { key: 'entity_code', label: 'Entity', render: (v: any) => v || 'All' },
    { key: 'gl_account', label: 'GL Account', render: (v: any) => v || '-' },
    { key: 'source', label: 'Source', render: (v: any) => <StatusBadge status={v === 'COA_AUTO' ? 'AUTO' : v} /> },
    { key: 'is_active', label: 'Status', render: (v: any) => <StatusBadge status={v ? 'ACTIVE' : 'INACTIVE'} /> },
    { key: 'id', label: 'Actions', render: (_v: any, row: any) => row.is_active ? (
      <ActionButton label="Deactivate" onClick={(e) => { e.stopPropagation(); handleDeactivate(row.id); }} variant="danger" size="sm" />
    ) : null },
  ];

  return (
    <PageLayout
      title="Bank Accounts"
      subtitle="Treasury master data — auto-detected from OFX uploads, Plaid Link, and GL activity"
      actions={[
        { label: 'Discover from GL', onClick: handleDiscoverFromGL, variant: 'secondary' },
        { label: 'Scan CoA', onClick: handleAutoDetect, variant: 'secondary' },
        { label: '+ Add Manual', onClick: () => setShowCreate(true) },
      ]}
    >
      {toast && <div style={{ position: 'fixed', top: 20, right: 20, zIndex: 9999, padding: '10px 20px', borderRadius: 8, fontSize: 13, fontWeight: 600, background: toast.ok ? '#22c55e' : '#ef4444', color: '#fff' }}>{toast.msg}</div>}

      {summary && (
        <KpiGrid items={[
          { label: 'Total Accounts', value: String(summary.total) },
          { label: 'Active', value: String(summary.active), color: '#22c55e' },
          ...Object.entries(summary.by_type || {}).map(([type, count]) => ({
            label: type, value: String(count),
          })),
        ]} />
      )}

      {suggestions.length > 0 && (
        <div style={{ marginBottom: 16, padding: 16, background: 'var(--bg-card)', border: '1px solid var(--accent)', borderRadius: 8 }}>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>
            Discovery suggestions
          </div>
          {suggestions.map((s, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderTop: i > 0 ? '1px solid var(--border-subtle)' : 'none' }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{s.evidence}</div>
                <div style={{ fontSize: 13, marginTop: 4 }}>
                  Propose: <strong>{s.proposed_bank_name}</strong> ({s.proposed_currency}, {s.proposed_account_type}
                  {s.proposed_entity_code ? `, entity ${s.proposed_entity_code}` : ''})
                </div>
              </div>
              <ActionButton label="Auto-create" onClick={() => handleApplySuggestion(s)} />
            </div>
          ))}
        </div>
      )}

      <DataTable
        columns={columns}
        rows={accounts}
        loading={loading}
        emptyMessage="No bank accounts registered yet. The AI-native paths populate this automatically: Upload an OFX statement (Bank Reconciliation > Upload OFX), connect Plaid (Treasury > Plaid Link), or click 'Discover from GL' to surface suggestions based on existing GL cash activity. Manual '+ Add Manual' is the last-resort fallback."
        rowKey="id"
      />

      <Modal open={showCreate} onClose={() => setShowCreate(false)} title="Add Bank Account">
        <div style={{ padding: '8px 12px', marginBottom: 12, background: 'var(--bg-subtle)', borderRadius: 6, fontSize: 12, color: 'var(--text-secondary)', border: '1px solid var(--border-subtle)' }}>
          We'll auto-create a per-bank GL sub-account under your Cash rollup (e.g.
          <strong> 1010-001 </strong>) so every bank reconciles independently --
          SAP / NetSuite / Oracle convention. Leave GL Account blank unless you
          want to link an existing manually-created sub-account.
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          {[
            ['Account Name *', 'account_name', 'e.g. Chase Operating USD'],
            ['Account Number', 'account_number', '****1234'],
            ['Bank Name', 'bank_name', 'JPMorgan Chase'],
            ['Routing Number', 'routing_number', '021000021'],
            ['IBAN', 'iban', 'GB29NWBK60161331926819'],
            ['SWIFT Code', 'swift_code', 'CHASUS33'],
            ['GL Account (optional)', 'gl_account', 'leave blank to auto-create'],
          ].map(([label, field, ph]) => (
            <div key={field as string}>
              <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>{label}</label>
              <input style={inputStyle} value={(form as any)[field as string]} onChange={e => setForm({ ...form, [field as string]: e.target.value })} placeholder={ph as string} />
            </div>
          ))}
          {/* Phase 9.6 -- Entity dropdown drives currency auto-fill. */}
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Entity</label>
            <select
              aria-label="Legal entity"
              style={inputStyle}
              value={form.entity_code}
              onChange={e => handleEntityChange(e.target.value)}
            >
              <option value="">-- Select entity --</option>
              {entities.map(e => (
                <option key={e.entity_code} value={e.entity_code}>
                  {e.entity_code} - {e.entity_name} ({e.functional_currency})
                </option>
              ))}
            </select>
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Account Type</label>
            <select aria-label="Account type" style={inputStyle} value={form.account_type} onChange={e => setForm({ ...form, account_type: e.target.value })}>
              <option value="OPERATING">Operating</option>
              <option value="PAYROLL">Payroll</option>
              <option value="SAVINGS">Savings</option>
              <option value="ESCROW">Escrow</option>
              <option value="PETTY_CASH">Petty Cash</option>
            </select>
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>
              Currency
              {currencyAutoFilled && form.currency && (
                <span style={{
                  marginLeft: 8, fontSize: 10, padding: '1px 6px',
                  borderRadius: 4, background: 'rgba(34,197,94,0.12)',
                  color: '#22c55e', textTransform: 'none',
                }}>
                  auto-filled from entity
                </span>
              )}
            </label>
            <select
              aria-label="Currency"
              style={inputStyle}
              value={form.currency}
              onChange={e => { setForm({ ...form, currency: e.target.value }); setCurrencyAutoFilled(false); }}
            >
              <option value="">-- (defaults from entity) --</option>
              <option value="USD">USD</option><option value="SAR">SAR</option><option value="BRL">BRL</option>
              <option value="EUR">EUR</option><option value="GBP">GBP</option>
              <option value="CAD">CAD</option><option value="AED">AED</option><option value="JPY">JPY</option>
              <option value="CNY">CNY</option><option value="INR">INR</option>
            </select>
          </div>
        </div>
        <div style={{ marginTop: 16, textAlign: 'right' }}>
          <ActionButton label="Create Account" onClick={handleCreate} />
        </div>
      </Modal>
    </PageLayout>
  );
}
